import React, { useState } from "react";
import { toast } from "sonner";
import { Wallet, Lightning } from "@phosphor-icons/react";
import {
  connectAndVerify,
  connectAndVerifyMobile,
  hasEthereumProvider,
  isMobile,
} from "../lib/wallet";
import { useAuth } from "../auth/AuthProvider";

export default function ConnectWalletButton({ variant = "header", onConnected }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    // Routing:
    //   - Mobile (Safari/Chrome on iOS/Android, no wallet DApp browser)
    //     → WalletConnect modal: stays on mfsmaioracle.com, deep-links to
    //       MetaMask/Trust/Rainbow app for sign, then bounces back.
    //   - Desktop OR mobile-inside-wallet-DApp-browser → use injected
    //     `window.ethereum` (the existing, well-tested flow).
    const onMobile = isMobile();
    const useWalletConnect = onMobile && !hasEthereumProvider();

    setBusy(true);
    const progressId = toast.loading("Opening wallet…", {
      description: useWalletConnect
        ? "Choose a wallet in the WalletConnect modal, then approve in the app."
        : "Approve the connection in your wallet.",
    });
    const hintTimer = setTimeout(() => {
      toast.loading("Waiting for signature…", {
        id: progressId,
        description: "Switch to your wallet app and approve the Sign-In message.",
      });
    }, 4000);

    try {
      const result = useWalletConnect
        ? await connectAndVerifyMobile()
        : await connectAndVerify();

      clearTimeout(hintTimer);
      toast.dismiss(progressId);

      await refresh();
      if (result?.pro?.is_pro) {
        toast.success("MFSMAI Pro unlocked", {
          description: `Verified ${Math.floor(result.pro.balance).toLocaleString()} MFSMAI on BSC.`,
        });
      } else {
        toast.info("Wallet connected (Basic tier)", {
          description: `You hold ${result.pro.balance.toFixed(2)} MFSMAI. Need 5,000+ to unlock Pro.`,
          duration: 6000,
        });
      }
      onConnected?.(result);
    } catch (e) {
      clearTimeout(hintTimer);
      toast.dismiss(progressId);
      if (e?.code === "USER_REJECTED") {
        toast.warning("Connection cancelled", {
          description: "You rejected the wallet prompt. Tap Connect again when ready.",
        });
      } else if (e?.code === "WALLET_TIMEOUT") {
        toast.error("Wallet timed out", {
          description: e.message || "The wallet didn't respond. Tap Connect again.",
          duration: 8000,
        });
      } else if (e?.code === "NO_WALLET_DESKTOP") {
        toast.error("No wallet extension", {
          description: "Install MetaMask at metamask.io, then refresh this page.",
          duration: 7000,
        });
      } else if (e?.code === "WALLET_WAKE_FAIL") {
        toast.error("MetaMask didn't respond", {
          description:
            "Click the MetaMask icon in your browser toolbar to unlock it, then click Connect again. If you have multiple wallet extensions installed (Rabby, Phantom, Coinbase), disable the extras — they fight for control and cause this.",
          duration: 11000,
        });
      } else {
        const msg =
          e?.response?.data?.detail || e?.shortMessage || e?.message || "Connect failed";
        toast.error("Wallet connect failed", { description: msg, duration: 7000 });
      }
    } finally {
      setBusy(false);
    }
  };

  if (variant === "header") {
    return (
      <button
        data-testid="connect-wallet-btn"
        onClick={handleClick}
        disabled={busy}
        className="inline-flex items-center gap-2 px-3 py-1.5 border border-edge/60 bg-edge/10 hover:bg-edge hover:text-black text-edge text-sm font-display uppercase tracking-[0.18em] transition-colors disabled:opacity-60"
      >
        <Wallet size={14} weight="fill" />
        {busy ? "Connecting…" : "Connect Wallet"}
      </button>
    );
  }
  return (
    <button
      data-testid="connect-wallet-btn-hero"
      onClick={handleClick}
      disabled={busy}
      className="inline-flex items-center gap-2 bg-edge text-black font-display text-sm font-semibold uppercase tracking-[0.18em] px-5 py-2.5 hover:bg-white transition-colors disabled:opacity-60"
    >
      <Lightning size={14} weight="fill" />
      {busy ? "Verifying on BSC…" : "Connect Wallet to unlock Pro"}
    </button>
  );
}
